import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';

export const FAQ = () => {
  const [openItems, setOpenItems] = useState<number[]>([]);

  const toggleItem = (index: number) => {
    setOpenItems(prev => 
      prev.includes(index) 
        ? prev.filter(item => item !== index)
        : [...prev, index]
    );
  };

  const faqData = [
    {
      category: "General",
      questions: [
        {
          question: "What is NINEEYE?",
          answer: "NINEEYE is your premier destination for quality fashion and lifestyle products. We bring you the latest trends with exceptional service and a commitment to customer satisfaction."
        },
        {
          question: "Where are you located?",
          answer: "We are based in Chennai, Tamil Nadu. Our address is 1st Floor, 36-07, Kamaraj Colony 2nd Street, Kodambakkam, Chennai, Chennai, Tamil Nadu, 600024."
        },
        {
          question: "How can I contact customer service?",
          answer: "You can reach us at +91 7094974571 or email us at contact@nineeyeshop.com. Our customer service team is available Monday to Saturday, 9 AM to 7 PM."
        }
      ]
    },
    {
      category: "Orders & Shipping",
      questions: [
        {
          question: "How long does shipping take?",
          answer: "Standard shipping takes 5-7 business days, while express shipping takes 2-3 business days. We offer free shipping on orders above ₹999."
        },
        {
          question: "Do you ship internationally?",
          answer: "Currently, we only ship within India. We're working on expanding our shipping to international destinations soon."
        },
        {
          question: "Can I track my order?",
          answer: "Yes! Once your order is dispatched, you'll receive a tracking number via SMS and email. You can track your order status in real-time on our website."
        },
        {
          question: "What if my order is delayed?",
          answer: "If your order is delayed beyond the estimated delivery time, please contact our customer service team. We'll investigate and provide updates on your order status."
        }
      ]
    },
    {
      category: "Returns & Exchanges",
      questions: [
        {
          question: "What is your return policy?",
          answer: "We offer a 30-day return policy for unused items in original packaging. Items must be in original condition with tags attached."
        },
        {
          question: "How do I return an item?",
          answer: "Contact us at contact@nineeyeshop.com or call +91 7094974571. We'll provide you with a return authorization number and instructions for returning the item."
        },
        {
          question: "Can I exchange for a different size?",
          answer: "Yes! We offer free size exchanges within 30 days of delivery. Contact us for exchange requests and we'll send the new size while you return the original item."
        },
        {
          question: "How long do refunds take?",
          answer: "Refunds are processed within 5-7 business days after we receive your return. You'll receive email confirmation once the refund is processed."
        }
      ]
    },
    {
      category: "Products",
      questions: [
        {
          question: "What sizes do you offer?",
          answer: "We offer sizes XS, S, M, L, XL, and XXL for clothing, and waist sizes 28, 30, 32, 34, 36, and 38 for pants. Size availability may vary by product."
        },
        {
          question: "Are your products authentic?",
          answer: "Yes, all our products are 100% authentic. We source directly from manufacturers and authorized distributors to ensure quality and authenticity."
        },
        {
          question: "Do you offer plus sizes?",
          answer: "We're working on expanding our size range to include plus sizes. Please contact us if you're looking for specific sizes not currently available."
        },
        {
          question: "Can I request a specific product?",
          answer: "Absolutely! If you're looking for a specific product that's not currently available, please contact us and we'll do our best to source it for you."
        }
      ]
    },
    {
      category: "Payment & Security",
      questions: [
        {
          question: "What payment methods do you accept?",
          answer: "We accept all major credit cards, debit cards, UPI, net banking, and digital wallets. All payments are processed securely through our payment partners."
        },
        {
          question: "Is my payment information secure?",
          answer: "Yes, we use industry-standard encryption and security measures to protect your payment information. We never store your complete payment details on our servers."
        },
        {
          question: "Can I pay with cash on delivery?",
          answer: "Currently, we don't offer cash on delivery. We accept all major digital payment methods for a secure and convenient shopping experience."
        },
        {
          question: "What if my payment fails?",
          answer: "If your payment fails, please check your card details and try again. If the issue persists, contact your bank or try a different payment method. Our customer service team is also available to help."
        }
      ]
    }
  ];

  let questionIndex = 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container py-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <Link 
              to="/" 
              className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-4"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Link>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h1>
            <p className="text-gray-600">Find answers to common questions about our products and services</p>
          </div>

          {/* FAQ Content */}
          <div className="space-y-8">
            {faqData.map((category, categoryIndex) => (
              <div key={categoryIndex} className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-2xl font-semibold text-gray-900 mb-6">{category.category}</h2>
                <div className="space-y-4">
                  {category.questions.map((item, itemIndex) => {
                    const currentIndex = questionIndex++;
                    const isOpen = openItems.includes(currentIndex);
                    
                    return (
                      <div key={itemIndex} className="border border-gray-200 rounded-lg">
                        <button
                          onClick={() => toggleItem(currentIndex)}
                          className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                        >
                          <span className="font-medium text-gray-900">{item.question}</span>
                          {isOpen ? (
                            <ChevronUp className="h-5 w-5 text-gray-500" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-gray-500" />
                          )}
                        </button>
                        {isOpen && (
                          <div className="px-6 pb-4">
                            <p className="text-gray-700 leading-relaxed">{item.answer}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Contact Section */}
          <div className="mt-12 bg-primary-50 border border-primary-200 rounded-lg p-8">
            <h2 className="text-2xl font-semibold text-primary-900 mb-4">Still Have Questions?</h2>
            <p className="text-primary-700 mb-6">
              Can't find the answer you're looking for? Our customer service team is here to help!
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-primary-900 mb-2">Contact Information</h3>
                <p className="text-primary-700">
                  <strong>Phone:</strong> +91 7094974571<br />
                  <strong>Email:</strong> contact@nineeyeshop.com<br />
                  <strong>Hours:</strong> Monday to Saturday, 9 AM to 7 PM
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-primary-900 mb-2">Visit Us</h3>
                <p className="text-primary-700">
                  1st Floor, 36-07, Kamaraj Colony 2nd Street,<br />
                  Kodambakkam, Chennai, Chennai, Tamil Nadu, 600024
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
